import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Endpoints} from "../endpoints";
import {catchError, firstValueFrom, Observable} from "rxjs";
import {HelperUtils} from "../../@admin/utils/methods/helper.utils";
import {
  AccountPeriodsCreateRequest,
  AccountPeriodUpdateRequest,
  CommonDataListRequest,
  DownloadValueSourceDataProcessingFileRequest,
  LoginRequest,
  LoginResponse,
  ManageCappsVehicleTypeRequest,
  ManageStlVehicleTypeRequest,
  ManageVehicleTypeMappingRequest,
  ProfileDetailsResponse,
  SyncVpicModelRequest,
  UtilityResponseResponse,
  ValueSourceDataProcessingRequest
} from "../models";
import {VehicleMappingResponse} from "../models/response/vehicle-mappings-response";
import {CappsVehicleTypesResponse} from "../models/response/capps-vehicle-types-response";
import {StlVehicleTypesResponse} from "../models/response/stl-vehicle-types-response";
import {UpsertModelResponse} from "../models/response/upsert-model-response";
import {AccountPeriodsResponseResponse} from "../models/response/settings-account-periods-response";
import {
  SyncVpicMakesResponseResponse,
  SyncVpicModelsResponseResponse
} from "../models/response/sync-vipc-data-response";
import {
  DownloadProcessStlDataResponseResponse,
  ProcessStlDataResponseResponse
} from "../models/response/process-stl-data-response";
import { EnumsResponse } from '../models/response/utility-response';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  constructor(private http: HttpClient, private _endpoints: Endpoints) {
  }

  getStateList(): Observable<any> {
    return this.http.get<UpsertModelResponse>('assets/base/data/stateList.json');
  }

  /**
   * DELETE : Delete Cache from Valuation Api
   * */
  getClearCache(): Observable<any> {
    return this.http
      .delete<UtilityResponseResponse>(this._endpoints.appendValuationApiBaseUrl(this._endpoints.clear_cache))
      .pipe(catchError(HelperUtils.handleError));
  }

  /* ---------- HttpClient API GET method => Get Dropdown ---------------  */
  getUtilityDropdownList(): Observable<any> {
    return this.http
      .get<UtilityResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_dropdown))
      .pipe(catchError(HelperUtils.handleError));
  }

  /**
   * GET : Account List Based On Account Type
   * */
  getAccountsList(accountTypeEnum: number): Promise<UpsertModelResponse> {
    const url = `${this._endpoints.appendApiBaseUrl(this._endpoints.utility_accounts)}?accountTypeEnum=${accountTypeEnum}`;
    return firstValueFrom(this.http.get<UpsertModelResponse>(url, {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * GET : Models based on make
   * */
  getModelsFromMake(makeId: number): Promise<UpsertModelResponse> {
    return firstValueFrom(this.http
      .get<UpsertModelResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_models_by_make_id) + makeId)
      .pipe(catchError(HelperUtils.handleError)));
  }

  getMakes(): Promise<UpsertModelResponse> {
    return firstValueFrom(this.http
      .get<UpsertModelResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_dropdown_makes))
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * POST : Login
   * */

  login(reqBody: LoginRequest): Promise<LoginResponse> {
    return firstValueFrom(this.http
      .post<LoginResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.get_Login), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * GET : Get Profile Details
   * */
  getProfileDetailsById(userId: string): Observable<ProfileDetailsResponse> {
    return this.http
      .get<ProfileDetailsResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_profile_details_by_id + userId))
      .pipe(catchError(HelperUtils.handleError));
  }

  /**
   * GET : Get Vehicle Mappings
   * */
  GetVehicleMappingsList(): Promise<VehicleMappingResponse> {
    return firstValueFrom(this.http
      .get<VehicleMappingResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_vehicle_types_mappings))
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * DELETE : Delete Vehicle Mappings
   * */
  deleteVehicleTypeMappings(vehicleTypeId: number): Promise<UpsertModelResponse> {
    const url = this._endpoints.appendApiBaseUrl(this._endpoints.utility_delete_vehicle_types_mapping) + vehicleTypeId
    return firstValueFrom(
      this.http.delete<UpsertModelResponse>(url).pipe(catchError(HelperUtils.handleError))
    );
  }

  ManageVehicleMappingsList(reqBody: ManageVehicleTypeMappingRequest): Promise<VehicleMappingResponse> {
    return firstValueFrom(this.http
      .post<VehicleMappingResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_manage_vehicle_types_mappings), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }


  /**
   * GET : Get CAPPS Vehicle Mappings
   * */
  GetCappsVehicleMappingsList(): Promise<CappsVehicleTypesResponse> {
    return firstValueFrom(this.http
      .get<CappsVehicleTypesResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_capps_vehicle_types_mappings))
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * DELETE : Delete CAPPS Vehicle Types
   * */
  deleteCappsVehicleTypeMappings(vehicleTypeId: number): Promise<UpsertModelResponse> {
    const url = this._endpoints.appendApiBaseUrl(this._endpoints.utility_delete_capps_vehicle_types_mapping) + vehicleTypeId
    return firstValueFrom(
      this.http.delete<UpsertModelResponse>(url).pipe(catchError(HelperUtils.handleError))
    );
  }

  /**
   * POST : Manage CAPPS Vehicle Mappings
   * */
  ManageCappsVehicleMappingsList(reqBody: ManageCappsVehicleTypeRequest): Promise<CappsVehicleTypesResponse> {
    return firstValueFrom(this.http
      .post<CappsVehicleTypesResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_manage_capps_vehicle_types_mappings), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * GET : Get CAPPS Vehicle Mappings
   * */
  GetStlVehicleMappingsList(): Promise<StlVehicleTypesResponse> {
    return firstValueFrom(this.http
      .get<StlVehicleTypesResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_stl_vehicle_types_mappings))
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * DELETE : Delete STL Vehicle Types
   * */
  deleteStlVehicleTypeMappings(vehicleTypeId: number): Promise<UpsertModelResponse> {
    const url = this._endpoints.appendApiBaseUrl(this._endpoints.utility_delete_stl_vehicle_types_mapping) + vehicleTypeId
    return firstValueFrom(
      this.http.delete<UpsertModelResponse>(url).pipe(catchError(HelperUtils.handleError))
    );
  }

  /**
   * POST : Manage STL Vehicle Mappings
   * */
  ManageStlVehicleMappingsList(reqBody: ManageStlVehicleTypeRequest): Promise<StlVehicleTypesResponse> {
    return firstValueFrom(this.http
      .post<StlVehicleTypesResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_manage_stl_vehicle_types_mappings), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }


  /**
   * Settings - Account Periods - List
   * **/
  GetAccountPeriodsList(reqBody: CommonDataListRequest): Promise<AccountPeriodsResponseResponse> {
    return firstValueFrom(this.http
      .post<AccountPeriodsResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_get_account_period_list), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Settings - Account Periods - Create
   * **/
  addAccountPeriodsList(reqBody: AccountPeriodsCreateRequest): Promise<AccountPeriodsResponseResponse> {
    return firstValueFrom(this.http
      .post<AccountPeriodsResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_add_account_period), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Settings - Account Periods - Update
   * **/
  updateAccountPeriodsList(reqBody: AccountPeriodUpdateRequest): Promise<AccountPeriodsResponseResponse> {
    return firstValueFrom(this.http
      .patch<AccountPeriodsResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_update_account_period), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Settings - Sync VPIC - Make
   * **/
  getSyncedMakeList(reqBody: CommonDataListRequest): Promise<SyncVpicMakesResponseResponse> {
    return firstValueFrom(this.http
      .post<SyncVpicMakesResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_synced_vpic_makes), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Settings - Sync VPIC - Makes
   * **/
  getSyncMakesByVpicVehicleTypes(vpicVehicleTypeId: number): Promise<SyncVpicMakesResponseResponse> {
    return firstValueFrom(this.http
      .get<SyncVpicMakesResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_sync_makes_by_vpic_VehicleTypeId) + vpicVehicleTypeId)
      .pipe(catchError(HelperUtils.handleError)));
  }


  /**
   * Settings - Sync VPIC - Models
   * **/
  getSyncedModelsList(reqBody: CommonDataListRequest): Promise<SyncVpicModelsResponseResponse> {
    return firstValueFrom(this.http
      .post<SyncVpicModelsResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_synced_vpic_models), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Settings - Sync VPIC - Models
   * **/
  getSyncModelsByVpicVehicleTypes(reqBody: SyncVpicModelRequest): Promise<SyncVpicModelsResponseResponse> {
    return firstValueFrom(this.http
      .post<SyncVpicModelsResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_sync_vpic_models), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Settings - Sync Make - Models - : VPIC Vehicle Type Dropdown
   * */
  getVpicVehicleTypeDropdownList(): Promise<UpsertModelResponse> {
    return firstValueFrom(this.http
      .get<UpsertModelResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_vpic_vehicle_type_dropdown), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Settings - Sync Make - Models - : VPIC Vehicle Type Dropdown
   * */
  getVpicVehicleMakeDropdownList(vpicVehicleTypeId: number): Promise<UpsertModelResponse> {
    return firstValueFrom(this.http
      .get<UpsertModelResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_vpic_make_dropdown) + vpicVehicleTypeId, {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Process STL Data - List
   * **/
  GetProcessStlDataList(reqBody: CommonDataListRequest): Promise<ProcessStlDataResponseResponse> {
    return firstValueFrom(this.http
      .post<ProcessStlDataResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_get_process_stl_data), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Process STL Data - Download File
   * **/
  GetDownloadProcessStlFile(reqBody: DownloadValueSourceDataProcessingFileRequest): Promise<DownloadProcessStlDataResponseResponse> {
    return firstValueFrom(this.http
      .post<DownloadProcessStlDataResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_download_process_stl_file), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /**
   * Process STL Data - Upload File
   * **/
  GetUploadProcessStlValueSource(reqBody: ValueSourceDataProcessingRequest): Promise<ProcessStlDataResponseResponse> {
    return firstValueFrom(this.http
      .post<ProcessStlDataResponseResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_upload_process_stl_file), JSON.stringify(reqBody), {headers: this._endpoints.httpHeader})
      .pipe(catchError(HelperUtils.handleError)));
  }

  /* ---------- HttpClient API GET method => Get Dropdown ---------------  */
  getEnumsDropdownList(): Observable<any> {
    return this.http
      .get<EnumsResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.umanage_county_portal_enums_dropdown))
      .pipe(catchError(HelperUtils.handleError));
  }

  /**
   * GET : Roles based on make
   * */
  getRolesList(): Promise<UpsertModelResponse> {
    return firstValueFrom(this.http
      .get<UpsertModelResponse>(this._endpoints.appendApiBaseUrl(this._endpoints.utility_dropdowns_admin_roles))
      .pipe(catchError(HelperUtils.handleError)));
  }
}
